/**
 * Реестр фиче-флагов приложения.
 *
 * Флаг разрешается в три шага (см. `resolveFeatureFlag`):
 *   1. override из localStorage (`imgame:ff:*`) — его ставит `?flow=` или вкладка настроек «Интерфейс»;
 *   2. дата регистрации пользователя (`user.created_at`) не раньше `since`;
 *   3. иначе флаг выключен.
 */

import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from 'shared/i18n/i18n';

export type FeatureFlagName = 'applicationsFlowV2' | 'applicationsFlow1447';

export interface FeatureFlagDefinition {
  /** Ключ в localStorage, по соглашению `imgame:ff:<имя флага>`. */
  storageKey: string;
  /** Подпись на вкладке настроек «Интерфейс» — её видит пользователь. */
  title: MessageDescriptor;
  /** Пояснение, что именно меняет флаг. */
  description: MessageDescriptor;
  /**
   * Дата отсечки: флаг включён у пользователей, зарегистрированных не раньше неё.
   * ISO-строка; сдвиг отсечки — правка одной константы, видимая в истории.
   */
  since: string;
}

/**
 * Дата старта эксперимента со вторым флоу вкладки «Заявки».
 */
export const APPLICATIONS_V2_SINCE = '2026-09-01T00:00:00Z';

/**
 * Не настоящая дата роллаута: `applicationsFlow1447` — A/B-тоггл ветки IMGM-1447 против
 * продакшена, а не эксперимент с постепенным включением по дате регистрации. Дата в далёком
 * будущем гарантирует «по умолчанию выключено» из общей `resolveFeatureFlag`, не дублируя её
 * логику отдельным механизмом — включается только вручную, на вкладке «Интерфейс» (там он виден только вне прода).
 */
export const APPLICATIONS_FLOW_1447_SINCE = '2099-01-01T00:00:00Z';

export const FEATURE_FLAGS: Record<FeatureFlagName, FeatureFlagDefinition> = {
  applicationsFlowV2: {
    storageKey: 'imgame:ff:applicationsFlowV2',
    title: msg`Новый интерфейс заявок`,
    description: msg`На вкладке «Заявки» сразу видны все команды и игроки, а не только незаявленные. Показано, сколько игроков уже заявлено из лимита турнира, и подсказано, что делать дальше.`,
    since: APPLICATIONS_V2_SINCE,
  },
  applicationsFlow1447: {
    storageKey: 'imgame:ff:applicationsFlow1447',
    title: msg`Новое оформление турнира`,
    description: msg`Вкладки турнира называются «О турнире», «Параметры» и «Команды». Состав команды открывается в окне поверх списка, а не на отдельной странице. Обновлены карточки команд и фильтры.`,
    since: APPLICATIONS_FLOW_1447_SINCE,
  },
};

export const FEATURE_FLAG_NAMES = Object.keys(FEATURE_FLAGS) as FeatureFlagName[];
